/**
 * How each PMS provider is *presented* to a user — its brand name and brand mark — and the one
 * capability every surface has to dispatch on: whether it can serve an inbound document import.
 *
 * **The single source of truth for what we call a provider.** Every sync stamp (the sidebar
 * "Synced with…" badge, the "Syncing with…" spinner, the "Sync to…" CTA, the Add-Document source,
 * the AI-Scribe document picker, the PMS review banner, the single-matter import modal) reads its
 * label and logo from here.
 *
 * It exists because the label used to be re-derived at each surface as
 * `source.charAt(0).toUpperCase() + source.slice(1)`. That happens to produce the right answer for
 * Actionstep, but a computed title-case can't be trusted for a brand that doesn't follow simple
 * capitalisation rules, so the mapping is declared once here rather than derived per surface.
 *
 * **Never title-case a provider slug by hand.** Add a provider by adding one entry here, and the
 * name and mark it is shown under follow everywhere at once.
 *
 * **Import this module directly, not via the `pms` barrel.** Several suites `jest.mock` that barrel
 * with a partial factory (`{ usePmsStatus }`), so a barrel-routed read resolves to `undefined` at
 * module-init time and takes the whole suite down with
 * `getPmsProviderPresentation is not a function`.
 */

import type { PmsProvider } from './types';

export interface PmsProviderPresentation {
  /** The PMS's brand name, exactly as the vendor writes it. */
  label: string;
  /**
   * Optional brand mark. A provider with no asset in `public/integrations/symbols/` omits this, and
   * its surfaces render their own neutral fallback rather than a broken image.
   */
  logoUrl?: string;
  /**
   * Whether this provider can serve an **inbound document import** — listing a matter's PMS
   * documents and downloading one.
   *
   * **This is the single definition of that capability**, and every surface offering to pull
   * documents from a PMS resolves to it: the matter layout's PMS review banner, the Extract
   * wizard's "Sync documents from {provider}" button, and the Add-Document `pms` source. The
   * matter-level conditions (the matter is actually synced, and to the firm's *current* provider)
   * layer on top in `matters/pms-sync.ts`'s `canImportDocumentsFromPms`, which reads this rather
   * than restating it.
   *
   * It **was** restated there, as a second provider array, and the two drifted: ActionStep read
   * `true` in `pms-sync.ts` and `false` here, so the Extract button worked while the review banner
   * was dead on every ActionStep matter (VALE-356). Add a provider by editing this one field.
   *
   * False means the provider has no working inbound legs, so the affordance could only ever end in
   * an error. Gate on this, never on whether a `pmsSource` is merely present.
   */
  supportsDocumentImport: boolean;
}

const PRESENTATION_BY_PROVIDER: Record<PmsProvider, PmsProviderPresentation> = {
  smokeball: {
    label: 'Smokeball',
    logoUrl: '/integrations/symbols/smokeball.png',
    supportsDocumentImport: true
  },
  actionstep: {
    // Lowercase s — "ActionStep" (interior capital) is a misspelling of the brand (VALE-641).
    label: 'Actionstep',
    logoUrl: '/integrations/symbols/actionstep.png',
    // True since LF-6408 landed `getFilesOnMatter` + `downloadFile` on `actionstep.provider.ts`.
    // This entry stayed `false` for the whole of that release, which is the VALE-356 bug.
    supportsDocumentImport: true
  }
};

/**
 * The presentation for a provider Vale is known to support, as a total function.
 *
 * Use this when the provider is already typed as a {@link PmsProvider} (a provider card, the import
 * modal's config) — every key is present by construction, so there is nothing to fall back to.
 * Surfaces reading a provider out of API data want {@link getPmsProviderPresentation} instead.
 */
export function getPmsProviderPresentationFor(
  provider: PmsProvider
): PmsProviderPresentation {
  return PRESENTATION_BY_PROVIDER[provider];
}

/**
 * The presentation for a provider, or `undefined` when the value is not one Vale recognises.
 *
 * Deliberately accepts a widened `string` rather than `PmsProvider`: callers read the provider from
 * API data (`matter.pmsProviderName`, `matter.metadata.pms.source`), which the backend types more
 * loosely than the frontend does — `pmsProviderName` still admits the unimplemented `clio` / `leap`,
 * and the backend's `PMS_SOURCES` can persist them. An unrecognised value must therefore be
 * expressible, and it **degrades to absent, never to Smokeball** (the PMS provider-isolation audit's
 * rule 4, LF-6342): showing one provider's brand for another is the misattribution this registry
 * exists to prevent.
 */
export function getPmsProviderPresentation(
  provider: string | null | undefined
): PmsProviderPresentation | undefined {
  if (!provider) return undefined;
  return PRESENTATION_BY_PROVIDER[provider as PmsProvider];
}

/**
 * The brand name for a provider, or `undefined` when the value is not one Vale recognises.
 *
 * Surfaces that must always render something supply their own fallback (`?? 'PMS'`); surfaces that
 * are hidden without a provider simply render nothing.
 */
export function getPmsProviderLabel(
  provider: string | null | undefined
): string | undefined {
  return getPmsProviderPresentation(provider)?.label;
}

/**
 * Whether a provider can serve an inbound document import — see {@link PmsProviderPresentation}'s
 * `supportsDocumentImport`, which this is the reader for.
 *
 * An unrecognised provider (never mapped, or a value the frontend doesn't type but the backend
 * can still persist — `clio` / `leap`) returns `false`, the same degrade-to-absent rule
 * {@link getPmsProviderPresentation} follows: no capability can be claimed for a provider Vale
 * doesn't know how to serve.
 */
export function getPmsProviderSupportsDocumentImport(
  provider: string | null | undefined
): boolean {
  return getPmsProviderPresentation(provider)?.supportsDocumentImport ?? false;
}
